/**
 * Support — user-facing support tickets.
 *
 * `/support` shows a list of the user's tickets + a "New ticket" button.
 * `/support/:id` shows the conversation thread + reply box.
 *
 * Bypasses moderation gating intentionally so suspended users can still
 * request help (banned users see this page as their entry point too).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LifeBuoy, ChevronLeft, Plus, MessageCircle, Send, X, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
    TICKET_CATEGORIES,
    TICKET_STATUS,
    createTicket,
    getTicketById,
    getTicketMessages,
    getUserTickets,
    replyToTicket,
} from "@/lib/supportStore";

const STATUS_BADGE = {
    [TICKET_STATUS.OPEN]: "bg-primary/10 text-primary border-primary/30",
    [TICKET_STATUS.PENDING]: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
    [TICKET_STATUS.RESOLVED]: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
    [TICKET_STATUS.CLOSED]: "bg-muted text-muted-foreground border-border",
};

const StatusPill = ({ status }) => (
    <span
        data-testid={`ticket-status-${status}`}
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${STATUS_BADGE[status] || STATUS_BADGE.open}`}
    >
        {status}
    </span>
);

const formatDate = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return iso; }
};

const Support = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [v, setV] = useState(0);
    const [showNew, setShowNew] = useState(false);

    const tickets = useMemo(() => (user ? getUserTickets(user.id) : []), [user, v]);

    if (!user) {
        return (
            <div className="screen items-center justify-center text-center p-6" data-testid="support-screen">
                <p className="text-[13px] text-muted-foreground">Sign in to contact support.</p>
            </div>
        );
    }

    return (
        <div className="screen" data-testid="support-screen">
            <header className="px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3 border-b border-border/60 flex items-center gap-2">
                <button
                    onClick={() => navigate("/profile/manage")}
                    data-testid="support-back-btn"
                    className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted active:scale-95"
                    aria-label="Back"
                >
                    <ChevronLeft size={20} />
                </button>
                <div className="flex-1">
                    <h1 className="font-display text-[16px] font-semibold inline-flex items-center gap-1.5">
                        <LifeBuoy size={15} className="text-primary" />
                        Support
                    </h1>
                    <p className="text-[11.5px] text-muted-foreground">We usually reply within 24 hours.</p>
                </div>
                <button
                    type="button"
                    data-testid="support-new-btn"
                    onClick={() => setShowNew(true)}
                    className="h-10 px-3 rounded-xl bg-primary text-primary-foreground text-[12px] font-medium inline-flex items-center gap-1.5"
                >
                    <Plus size={14} />
                    New
                </button>
            </header>

            <div className="flex-1 overflow-y-auto no-scrollbar p-4">
                {tickets.length === 0 ? (
                    <div
                        data-testid="support-empty"
                        className="rounded-2xl border border-dashed border-border bg-card/40 p-6 text-center"
                    >
                        <span className="mx-auto h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-3">
                            <MessageCircle size={18} />
                        </span>
                        <h2 className="font-display text-[14px] font-semibold">No tickets yet</h2>
                        <p className="text-[12.5px] text-muted-foreground mt-1">
                            Open a ticket and the Neyra team will get back to you.
                        </p>
                        <button
                            type="button"
                            data-testid="support-empty-cta"
                            onClick={() => setShowNew(true)}
                            className="mt-4 h-10 px-4 rounded-xl bg-primary text-primary-foreground text-[12.5px] font-medium"
                        >
                            Create a ticket
                        </button>
                    </div>
                ) : (
                    <ul className="space-y-2.5" data-testid="support-list">
                        {tickets.map((t) => (
                            <li key={t.id}>
                                <button
                                    data-testid={`support-row-${t.id}`}
                                    onClick={() => navigate(`/support/${t.id}`)}
                                    className="w-full text-left p-3.5 rounded-2xl border border-border bg-card hover:bg-accent/40 transition-colors"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="font-display text-[13.5px] font-semibold truncate">{t.subject}</div>
                                        <StatusPill status={t.status} />
                                    </div>
                                    <div className="text-[11.5px] text-muted-foreground mt-1 truncate">
                                        {t.category} · Updated {formatDate(t.updated_at)}
                                    </div>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {showNew && (
                <NewTicketModal
                    user={user}
                    onClose={() => setShowNew(false)}
                    onCreated={(ticket) => {
                        setShowNew(false);
                        setV((x) => x + 1);
                        navigate(`/support/${ticket.id}`);
                    }}
                />
            )}
        </div>
    );
};

const NewTicketModal = ({ user, onClose, onCreated }) => {
    const [category, setCategory] = useState(TICKET_CATEGORIES[0]);
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [attachment, setAttachment] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const onFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 4 * 1024 * 1024) {
            setError("Attachment must be under 4 MB.");
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => setAttachment(ev.target.result);
        reader.readAsDataURL(f);
    };

    const submit = (e) => {
        e?.preventDefault?.();
        if (busy) return;
        setError(null);
        setBusy(true);
        const res = createTicket({ user, category, subject, message, attachment });
        setBusy(false);
        if (!res.ok) {
            setError(res.error);
            return;
        }
        onCreated(res.ticket);
    };

    return (
        <div
            data-testid="support-new-modal"
            className="fixed inset-0 z-[120] bg-black/55 flex items-end sm:items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="w-full max-w-sm rounded-2xl bg-card border border-border shadow-card p-5 max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display text-[16px] font-semibold inline-flex items-center gap-2">
                        <LifeBuoy size={15} className="text-primary" />
                        New ticket
                    </h3>
                    <button
                        type="button"
                        data-testid="support-new-close"
                        onClick={onClose}
                        className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>
                <form onSubmit={submit} className="space-y-3" autoComplete="off">
                    <label className="block">
                        <span className="block text-[11.5px] font-medium text-muted-foreground mb-1.5 ml-1">Category</span>
                        <select
                            data-testid="support-new-category"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="w-full h-11 px-3 rounded-xl bg-secondary border border-border focus:border-primary focus:bg-background outline-none text-[13.5px]"
                        >
                            {TICKET_CATEGORIES.map((c) => (
                                <option key={c}>{c}</option>
                            ))}
                        </select>
                    </label>
                    <label className="block">
                        <span className="block text-[11.5px] font-medium text-muted-foreground mb-1.5 ml-1">Subject</span>
                        <input
                            data-testid="support-new-subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            maxLength={120}
                            placeholder="Short summary"
                            className="w-full h-11 px-3.5 rounded-xl bg-secondary border border-border focus:border-primary focus:bg-background outline-none text-[13.5px]"
                        />
                    </label>
                    <label className="block">
                        <span className="block text-[11.5px] font-medium text-muted-foreground mb-1.5 ml-1">Message</span>
                        <textarea
                            data-testid="support-new-message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={5}
                            placeholder="Tell us what's going on…"
                            className="w-full px-3.5 py-2.5 rounded-xl bg-secondary border border-border focus:border-primary focus:bg-background outline-none text-[13.5px] resize-y"
                        />
                    </label>
                    <label className="block">
                        <span className="block text-[11.5px] font-medium text-muted-foreground mb-1.5 ml-1">Attachment (optional, max 4 MB)</span>
                        <input
                            data-testid="support-new-file"
                            type="file"
                            accept="image/*,.pdf"
                            onChange={onFile}
                            className="block w-full text-[12px] file:mr-3 file:rounded-lg file:border-0 file:bg-secondary file:text-foreground file:px-3 file:h-9"
                        />
                        {attachment && (
                            <div className="mt-1 text-[11px] text-muted-foreground inline-flex items-center gap-1">
                                Attached
                                <button
                                    type="button"
                                    onClick={() => setAttachment(null)}
                                    className="text-destructive underline ml-1"
                                >
                                    remove
                                </button>
                            </div>
                        )}
                    </label>
                    {error && (
                        <p data-testid="support-new-error" className="text-[12px] text-destructive font-medium inline-flex items-center gap-1">
                            <AlertCircle size={12} /> {error}
                        </p>
                    )}
                    <div className="flex items-center gap-2 pt-1">
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="support-new-cancel"
                            className="flex-1 h-11 rounded-xl border border-border bg-secondary text-[13px] font-medium hover:bg-muted"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            data-testid="support-new-submit"
                            disabled={busy}
                            className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground text-[13px] font-medium disabled:opacity-60"
                        >
                            {busy ? "Sending…" : "Submit"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export const SupportTicket = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user } = useAuth();
    const [v, setV] = useState(0);
    const [draft, setDraft] = useState("");

    const ticket = useMemo(() => getTicketById(id), [id, v]);
    const messages = useMemo(() => (ticket ? getTicketMessages(ticket.id) : []), [ticket, v]);

    useEffect(() => { /* re-render when route changes */ }, [id]);

    if (!ticket) {
        return (
            <div className="screen items-center justify-center p-5 text-center" data-testid="support-ticket-missing">
                <p className="text-[13px] text-muted-foreground">Ticket not found.</p>
                <button
                    onClick={() => navigate("/support")}
                    className="mt-3 h-10 px-4 rounded-xl bg-primary text-primary-foreground text-[12.5px] font-medium"
                >
                    Back to support
                </button>
            </div>
        );
    }

    const isClosed = ticket.status === TICKET_STATUS.CLOSED;
    const send = () => {
        if (!draft.trim() || isClosed || !user) return;
        replyToTicket({ ticket, author: user, role: "user", body: draft });
        setDraft("");
        setV((x) => x + 1);
    };

    return (
        <div className="screen" data-testid="support-ticket-screen">
            <header className="px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3 border-b border-border/60 flex items-center gap-2">
                <button
                    onClick={() => navigate("/support")}
                    data-testid="support-ticket-back"
                    className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted active:scale-95"
                    aria-label="Back"
                >
                    <ChevronLeft size={20} />
                </button>
                <div className="flex-1 min-w-0">
                    <h1 className="font-display text-[15px] font-semibold truncate">{ticket.subject}</h1>
                    <p className="text-[11px] text-muted-foreground truncate">{ticket.category}</p>
                </div>
                <StatusPill status={ticket.status} />
            </header>

            <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
                {messages.map((m) => (
                    <div
                        key={m.id}
                        data-testid={`support-msg-${m.id}`}
                        className={[
                            "max-w-[80%] rounded-2xl px-3.5 py-2.5 text-[13px]",
                            m.author_role === "user"
                                ? "ml-auto bg-primary text-primary-foreground"
                                : "bg-secondary border border-border",
                        ].join(" ")}
                    >
                        <div className="text-[10.5px] uppercase tracking-wider opacity-75 mb-0.5">
                            {m.author_role === "user" ? "You" : "Support"} · {formatDate(m.created_at)}
                        </div>
                        <div className="whitespace-pre-wrap">{m.body}</div>
                    </div>
                ))}
                {ticket.attachment && (
                    <div
                        data-testid="support-ticket-attachment"
                        className="rounded-xl border border-border bg-card p-2.5 mx-auto max-w-[200px]"
                    >
                        <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground mb-1.5">Attachment</div>
                        {String(ticket.attachment).startsWith("data:image/") ? (
                            <img src={ticket.attachment} alt="" className="w-full rounded-lg" />
                        ) : (
                            <a href={ticket.attachment} target="_blank" rel="noreferrer" className="text-[12px] text-primary underline break-all">
                                Open attachment
                            </a>
                        )}
                    </div>
                )}
            </div>

            <div className="p-3 border-t border-border bg-background">
                {isClosed ? (
                    <p className="text-center text-[12px] text-muted-foreground">
                        This ticket is closed. Open a new one if you still need help.
                    </p>
                ) : (
                    <div className="flex items-center gap-2">
                        <input
                            data-testid="support-ticket-reply-input"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && send()}
                            placeholder="Reply…"
                            className="flex-1 h-11 px-3.5 rounded-xl bg-secondary border border-border focus:border-primary focus:bg-background outline-none text-[13.5px]"
                        />
                        <button
                            type="button"
                            data-testid="support-ticket-reply-send"
                            onClick={send}
                            className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center"
                            aria-label="Send"
                        >
                            <Send size={16} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Support;
